import { AdresseModel, EmailModel, KontaktdatenRequestModel, TelefonModel } from "../../../api";
import { MitgliedschaftKontaktEditingAreaEnum } from "../enum/MitgliedschaftViewStatsEnums";
import { ExtendedKontaktdaten } from "../model/Kontaktdaten";
import { KontaktdatenValidationModel } from "../model/PropsInterfaces";

export const checkForError = (
  validationError: KontaktdatenValidationModel | undefined,
  fieldName: keyof KontaktdatenValidationModel,
  value: string | undefined,
  validationErrors: KontaktdatenValidationModel | undefined,
  kontaktdatenChanged: ExtendedKontaktdaten | undefined,
  required: boolean
) => {
  let validationResult: KontaktdatenValidationModel;
  if (validationErrors) {
    validationResult = { ...validationErrors };
  } else {
    validationResult = { ...validationError };
  }

  if (fieldName === "plz") {
    if (!value) {
      if (required) {
        validationResult[fieldName] = "Die Postleitzahl darf nicht leer sein.";
      }
    } else {
      if (kontaktdatenChanged?.staat === "AT" && !/^[0-9]{4}$/i.test(value)) {
        validationResult[fieldName] = "Die Postleitzahl muss in Österreich aus genau 4 Zahlen bestehen.";
      } else {
        validationResult[fieldName] = undefined;
      }
    }
  }
  if (fieldName === "ort") {
    if (!value) {
      if (required) {
        validationResult[fieldName] = "Der Ort darf nicht leer sein.";
      }
    } else {
      validationResult[fieldName] = undefined;
    }
  }
  if (fieldName === "strasse") {
    if (!value && !kontaktdatenChanged?.postfach) {
      if (required) {
        validationResult[fieldName] = "Die Strasse darf nicht leer sein.";
      }
    } else {
      validationResult[fieldName] = undefined;
    }
  }
  if (fieldName === "hausnummer") {
    if (!value && !kontaktdatenChanged?.postfach) {
      if (required) {
        validationResult[fieldName] = "Die Hausnummer darf nicht leer sein.";
      }
    } else if (value && !/^[0-9]*$/i.test(value)) {
      validationResult[fieldName] =
        "Die Hausnummer darf nur Zahlenwerte enthalten. Bitte verwenden Sie das Feld Zusatz für sämtliche anderen Werte.";
    } else {
      validationResult[fieldName] = undefined;
    }
  }
  if (fieldName === "zusatz") {
    if (value && !/^[0-9a-zA-Z].*$/i.test(value)) {
      validationResult[fieldName] = "Der Zusatz darf nur mit einem Zahlen- oder Buchstabenwert beginnen.";
    } else {
      validationResult[fieldName] = undefined;
    }
  }
  if (fieldName === "postfach") {
    if (value) {
      validationResult.strasse = undefined;
      validationResult.hausnummer = undefined;
    } else {
      if (!kontaktdatenChanged?.strasse) {
        validationResult.strasse = "Die Strasse darf nicht leer sein.";
      }
      if (!kontaktdatenChanged?.hausnummer) {
        validationResult.hausnummer = "Die Hausnummer darf nicht leer sein.";
      }
    }
  }

  if (fieldName === "email") {
    if (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i.test(value)) {
      validationResult[fieldName] = "Bitte geben Sie eine korrekte Email Adresse an. Beispiel: muster@wko.at";
    } else {
      validationResult[fieldName] = undefined;
    }
  }

  if (fieldName === "tel_land" || fieldName === "mobil_land") {
    if (!value) {
      if (required) {
        validationResult[fieldName] = "Die Ländervorwahl darf nicht leer sein.";
      }
    } else if (value && !/^([+]|00)[0-9]{2,5}$/i.test(value)) {
      validationResult[fieldName] = "Bitte geben Sie eine korrekte Ländervorwahl ein. Beispiel: +43";
    } else {
      validationResult[fieldName] = undefined;
    }
  }

  if (fieldName === "tel_vorwahl" || fieldName === "mobil_vorwahl") {
    if (value && !/^[1-9][0-9]{0,5}$/i.test(value)) {
      validationResult[fieldName] = "Bitte geben Sie eine korrekte Vorwahl ein. Nur Zahlen und ohne die führende 0.";
    } else {
      validationResult[fieldName] = undefined;
    }
  }

  if (fieldName === "tel_nummer" || fieldName === "mobil_nummer") {
    if (value && !/^[0-9]{2,14}$/i.test(value)) {
      validationResult[fieldName] = "Bitte geben Sie nur Zahlenwerte für die Telefonnummer an, mindestens 2 Stellen.";
    } else {
      validationResult[fieldName] = undefined;
    }
  }

  return validationResult;
};

export const validateValues = (
  area: MitgliedschaftKontaktEditingAreaEnum,
  validationError: KontaktdatenValidationModel | undefined,
  setValidationError: (value: KontaktdatenValidationModel | undefined) => void,
  kontaktdatenChanged: ExtendedKontaktdaten | undefined,
  required: boolean
): boolean => {
  if (area === MitgliedschaftKontaktEditingAreaEnum.ADDRESS) {
    let validationResult = checkForError(
      validationError,
      "hausnummer",
      kontaktdatenChanged?.hausnummer,
      undefined,
      kontaktdatenChanged,
      required
    );
    validationResult = checkForError(
      validationError,
      "plz",
      kontaktdatenChanged?.plz,
      validationResult,
      kontaktdatenChanged,
      required
    );
    validationResult = checkForError(
      validationError,
      "ort",
      kontaktdatenChanged?.ort,
      validationResult,
      kontaktdatenChanged,
      required
    );
    validationResult = checkForError(
      validationError,
      "strasse",
      kontaktdatenChanged?.strasse,
      validationResult,
      kontaktdatenChanged,
      required
    );

    if (checkIfValidationResultHasNoError(validationResult)) {
      setValidationError(undefined);
      return true;
    } else {
      setValidationError(validationResult);
      return false;
    }
  }
  return true;
};

export const checkIfValidationResultHasNoError = (validationResult: KontaktdatenValidationModel): boolean => {
  return Object.keys(validationResult).every((key) => {
    const dynamicField: keyof KontaktdatenValidationModel = key as keyof KontaktdatenValidationModel;
    return validationResult[dynamicField] === undefined;
  });
};

export const compareChanges = (
  area: MitgliedschaftKontaktEditingAreaEnum,
  kontaktdatenWKOBase: ExtendedKontaktdaten | undefined,
  kontaktdatenChanged: ExtendedKontaktdaten | undefined,
  manuelleEingabe: boolean
): KontaktdatenRequestModel | undefined => {
  let changed: boolean = false;
  let kontaktdatenChangeRequestModel: KontaktdatenRequestModel = {};

  if (area === MitgliedschaftKontaktEditingAreaEnum.ADDRESS) {
    let zusendeadresseAlt: AdresseModel = {};
    let zusendeadresseNeu: AdresseModel = {};

    zusendeadresseAlt.landIso = kontaktdatenWKOBase?.staat;
    zusendeadresseNeu.landIso = kontaktdatenChanged?.staat;
    if (kontaktdatenWKOBase?.staat !== kontaktdatenChanged?.staat) {
      changed = true;
    }

    zusendeadresseAlt.plz = kontaktdatenWKOBase?.plz;
    zusendeadresseNeu.plz = kontaktdatenChanged?.plz;
    if (kontaktdatenWKOBase?.plz !== kontaktdatenChanged?.plz) {
      changed = true;
    }

    zusendeadresseAlt.ort = kontaktdatenWKOBase?.ort;
    zusendeadresseNeu.ort = kontaktdatenChanged?.ort;
    if (kontaktdatenWKOBase?.ort !== kontaktdatenChanged?.ort) {
      changed = true;
    }

    zusendeadresseAlt.strasse = kontaktdatenWKOBase?.strasse;
    zusendeadresseNeu.strasse = kontaktdatenChanged?.strasse;
    if (kontaktdatenWKOBase?.strasse !== kontaktdatenChanged?.strasse) {
      changed = true;
    }

    zusendeadresseAlt.strasseSa = kontaktdatenWKOBase?.strasseSA;
    if (!manuelleEingabe) {
      zusendeadresseNeu.strasseSa = kontaktdatenChanged?.strasseSA;
    }
    if (kontaktdatenWKOBase?.strasseSA !== kontaktdatenChanged?.strasseSA) {
      changed = true;
    }

    zusendeadresseAlt.hausnummer = kontaktdatenWKOBase?.hausnummer;
    zusendeadresseNeu.hausnummer = kontaktdatenChanged?.hausnummer;
    if (zusendeadresseAlt.hausnummer !== zusendeadresseNeu.hausnummer) {
      changed = true;
    }

    zusendeadresseAlt.zusatz = kontaktdatenWKOBase?.zusatz;
    zusendeadresseNeu.zusatz = kontaktdatenChanged?.zusatz;
    if (zusendeadresseNeu.zusatz?.trim() === "") {
      zusendeadresseNeu.zusatz = undefined;
    }
    if (zusendeadresseAlt.zusatz !== zusendeadresseNeu.zusatz) {
      changed = true;
    }

    zusendeadresseAlt.postfach = kontaktdatenWKOBase?.postfach;
    zusendeadresseNeu.postfach = kontaktdatenChanged?.postfach?.trim();
    if (zusendeadresseNeu.postfach === "") {
      zusendeadresseNeu.postfach = undefined;
    }
    if (zusendeadresseAlt.postfach !== zusendeadresseNeu.postfach) {
      changed = true;
    }

    if (changed) {
      kontaktdatenChangeRequestModel = {
        zusendeadresseAlt,
        zusendeadresseNeu,
      };
    }
  } else if (area === MitgliedschaftKontaktEditingAreaEnum.PHONE) {
    let festnetzAlt: TelefonModel = { typ: "Festnetz" };
    let festnetzNeu: TelefonModel = { typ: "Festnetz" };

    festnetzAlt.nummer = kontaktdatenWKOBase?.tel;
    festnetzNeu.nummer = kontaktdatenChanged?.tel?.trim();
    if (festnetzNeu.nummer === "") {
      festnetzNeu.nummer = undefined;
    }
    if (festnetzAlt.nummer !== festnetzNeu.nummer) {
      changed = true;
    }
    if (changed) {
      kontaktdatenChangeRequestModel = {
        festnetzAlt,
        festnetzNeu,
      };
    }
  } else if (area === MitgliedschaftKontaktEditingAreaEnum.MOBILE) {
    let mobilAlt: TelefonModel = { typ: "Mobil" };
    let mobilNeu: TelefonModel = { typ: "Mobil" };
    mobilAlt.nummer = kontaktdatenWKOBase?.mobil;
    mobilNeu.nummer = kontaktdatenChanged?.mobil?.trim();
    if (mobilNeu.nummer === "") {
      mobilNeu.nummer = undefined;
    }
    if (mobilAlt.nummer !== mobilNeu.nummer) {
      changed = true;
    }
    if (changed) {
      kontaktdatenChangeRequestModel = {
        mobilAlt,
        mobilNeu,
      };
    }
  } else if (area === MitgliedschaftKontaktEditingAreaEnum.EMAIL) {
    let emailAlt: EmailModel = {};
    let emailNeu: EmailModel = {};

    emailAlt.emailAdresse = kontaktdatenWKOBase?.email;
    emailNeu.emailAdresse = kontaktdatenChanged?.email?.trim();
    if (emailNeu.emailAdresse === "") {
      emailNeu.emailAdresse = undefined;
    }
    if (emailAlt.emailAdresse !== emailNeu.emailAdresse) {
      changed = true;
    }

    if (changed) {
      kontaktdatenChangeRequestModel = {
        emailAlt,
        emailNeu,
      };
    }
  }

  if (changed) {
    return kontaktdatenChangeRequestModel;
  } else {
    return undefined;
  }
};
